import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { AxiosError } from "axios";
import * as authApi from "../apis/auth";
import * as adminApi from "../apis/admin";
import { LoginRequestDto } from "../dto/AdmDto";

/**
 * 슈퍼어드민 사용자 정보 인터페이스
 */
export interface AdminUser {
  admNm: string; // 관리자 이름
  admId?: string; // 관리자 아이디
  admIdntfcId?: number; // 관리자 식별 ID
  admAuth?: string; // 관리자 권한
}

/**
 * 슈퍼어드민 인증 상태 인터페이스
 */
interface AuthState {
  // 사용자 상태
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // 액션
  setUser: (user: AdminUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // 인증 관련 액션
  login: (credentials: {
    admId: string;
    pwd: string;
  }) => Promise<{ success: boolean; error?: string }>;

  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  initializeAuth: () => Promise<void>;
  reset: () => void;
}

/**
 * 슈퍼어드민 인증 상태 관리 Store
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      // 초기 상태
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      // 사용자 설정
      setUser: (user) => {
        console.log("🔧 슈퍼어드민 authStore.setUser:", user);
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      // 로딩 상태 설정
      setLoading: (isLoading) => {
        console.log("🔧 슈퍼어드민 authStore.setLoading:", isLoading);
        set({ isLoading });
      },

      // 에러 상태 설정
      setError: (error) => {
        console.log("🔧 슈퍼어드민 authStore.setError:", error);
        set({ error });
      },

      // 로그인
      login: async (credentials) => {
        console.log("🔐 슈퍼어드민 authStore.login 시작:", credentials.admId);
        set({ isLoading: true, error: null });

        try {
          const loginRequest: LoginRequestDto = {
            admId: credentials.admId,
            pwd: credentials.pwd,
          };

          // 🔥 쿠키 기반 로그인 - 토큰은 서버에서 자동 설정
          console.log("📡 슈퍼어드민 로그인 API 호출 중...");
          const result = await authApi.login(loginRequest);
          console.log("✅ 슈퍼어드민 authStore.login API 성공:", result);

          // 🔥 로그인 성공 후 사용자 정보 조회
          console.log("👤 슈퍼어드민 사용자 정보 조회 중...");
          await get().getCurrentUser();
          console.log("✅ 슈퍼어드민 사용자 정보 조회 완료");

          set({ isLoading: false, error: null });
          return { success: true };
        } catch (error) {
          console.error("❌ 슈퍼어드민 authStore.login 실패:", error);

          let errorMessage = "로그인에 실패했습니다.";

          // 🔥 개발 환경에서 상세한 에러 정보 추출
          if (process.env.NODE_ENV === "development") {
            console.log("🔍 슈퍼어드민 로그인 에러 상세 분석:");
            console.log("- 에러 타입:", typeof error);
            console.log("- 에러 객체:", error);
          }

          if (error instanceof AxiosError) {
            const responseData = error.response?.data;

            if (responseData?.details?.errorMessage) {
              errorMessage = responseData.details.errorMessage;
            } else if (responseData?.message) {
              errorMessage = responseData.message;
            } else if (responseData?.error) {
              errorMessage = responseData.error;
            } else if (error.message) {
              errorMessage = error.message;
            }

            console.log("🔍 슈퍼어드민 최종 에러 메시지:", errorMessage);
          }

          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            user: null,
          });

          return { success: false, error: errorMessage };
        }
      },

      // 로그아웃
      logout: async () => {
        console.log("🚪 슈퍼어드민 authStore.logout 시작");
        set({ isLoading: true });

        try {
          // 🔥 쿠키 기반 로그아웃 - 서버에서 쿠키 제거
          console.log("📡 슈퍼어드민 로그아웃 API 호출 중...");
          await authApi.logout();
          console.log("✅ 슈퍼어드민 authStore.logout API 성공");
        } catch (error) {
          console.warn(
            "⚠️ 슈퍼어드민 authStore.logout API 실패 (상태는 초기화):",
            error
          );
        } finally {
          // API 실패해도 클라이언트 상태는 초기화
          get().reset();
        }
      },

      // 현재 사용자 정보 조회
      getCurrentUser: async () => {
        console.log("👤 슈퍼어드민 authStore.getCurrentUser 시작");

        try {
          console.log("📡 슈퍼어드민 관리자 정보 API 호출 중...");
          const adminInfo = await adminApi.getAdminNmByToken();
          console.log(
            "✅ 슈퍼어드민 authStore.getCurrentUser API 성공:",
            adminInfo
          );

          const user: AdminUser = {
            admNm: adminInfo.admNm,
            admIdntfcId: adminInfo.admIdntfcId,
          };

          set({
            user,
            isAuthenticated: true,
            error: null,
          });

          console.log("✅ 슈퍼어드민 사용자 정보 설정 완료:", user);
        } catch (error) {
          console.error("❌ 슈퍼어드민 authStore.getCurrentUser 실패:", error);

          // 🔥 인증 오류 시 상태 초기화
          set({
            user: null,
            isAuthenticated: false,
            error: "사용자 정보를 가져올 수 없습니다.",
          });

          throw error;
        }
      },

      // 인증 상태 확인
      checkAuth: async () => {
        console.log("🔍 슈퍼어드민 authStore.checkAuth 시작");

        try {
          await get().getCurrentUser();
          console.log("✅ 슈퍼어드민 authStore.checkAuth 성공");
          return true;
        } catch (error) {
          // 🔥 인증 실패 시 자세한 로그
          const axiosError = error as AxiosError;
          const isTokenExpired = axiosError?.response?.status === 401;

          if (process.env.NODE_ENV === "development") {
            console.warn("❌ 슈퍼어드민 authStore.checkAuth 실패:", {
              status: axiosError?.response?.status,
              message: axiosError?.message,
              isTokenExpired,
            });
          } else {
            console.warn("❌ 슈퍼어드민 인증 확인 실패");
          }

          return false;
        }
      },

      // 인증 초기화 (앱 시작 시 호출)
      initializeAuth: async () => {
        console.log("🚀 슈퍼어드민 authStore.initializeAuth 시작");
        set({ isLoading: true, error: null });

        try {
          const isValid = await get().checkAuth();
          console.log("🔍 슈퍼어드민 initializeAuth 인증 상태:", isValid);

          if (isValid) {
            console.log("✅ 슈퍼어드민 initializeAuth 완료 - 인증됨");
          } else {
            console.log("⚠️ 슈퍼어드민 initializeAuth 완료 - 인증되지 않음");
          }
        } catch (error) {
          console.warn(
            "⚠️ 슈퍼어드민 authStore.initializeAuth: 인증 실패",
            error
          );

          // 🔥 에러 발생 시 명확한 상태 설정
          set({
            user: null,
            isAuthenticated: false,
            error: null, // 초기화 에러는 사용자에게 보여주지 않음
          });
        } finally {
          set({ isLoading: false });
        }
      },

      // 상태 초기화
      reset: () => {
        console.log("🔄 슈퍼어드민 authStore.reset");
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: "super-admin-auth-store", // 🔥 스토어 이름 구분
    }
  )
);
